/**
 * Netflix Academy — gedeelde stijl & rekenregels.
 *
 * Rijkleur (hover-gloed): Rij 1 Mentaal = blauw, Rij 2 Fysiek = rood,
 * Rij 3 Professioneel = groen, Rij 4 Passie/Hobby = oranje, Financieel = teal.
 */
import { Laptop, Landmark, BookOpen } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { AcademyLeervorm, AcademyTraining, Wallet, WkrLabel } from '../../services/medewerkerApi';

export const eur = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

/** "€ 150,-" voor ronde bedragen, anders "€ 142,50" — zoals op de tegel. */
export function prijs(bedrag: number): string {
  return Number.isInteger(bedrag) ? `${eur.format(bedrag).replace(/,00$/, '')},-` : eur.format(bedrag);
}

export const WKR_ORANJE = '#FF9800';

export type RijAccent = 'mentaal' | 'fysiek' | 'professioneel' | 'passie' | 'financieel';

export const ACCENT_KLEUR: Record<RijAccent, string> = {
  mentaal: '#3B82F6',
  fysiek: '#EF4444',
  professioneel: '#22C55E',
  passie: WKR_ORANJE,
  financieel: '#14B8A6',
};

/** Rij 4 (Persoonlijke Passie & Hobby's): groei die op de WKR-vrije ruimte drukt. */
export function isHobby(t: Pick<AcademyTraining, 'academy_categorie' | 'wkr_label'>): boolean {
  return t.academy_categorie === 'GROWTH' && t.wkr_label === 'wkr_vrije_ruimte';
}

export function accentVoor(t: Pick<AcademyTraining, 'academy_categorie' | 'wkr_label'>): RijAccent {
  switch (t.academy_categorie) {
    case 'MENTAL':
      return 'mentaal';
    case 'PHYSICAL':
      return 'fysiek';
    case 'FINANCIAL':
      return 'financieel';
    default:
      return isHobby(t) ? 'passie' : 'professioneel';
  }
}

export const LEERVORM: Record<AcademyLeervorm, { label: string; icon: LucideIcon; badge: string }> = {
  e_learning: { label: 'E-Learning', icon: Laptop, badge: 'bg-blue-600 shadow-blue-900/40' },
  workshop: { label: 'Workshop', icon: Landmark, badge: 'bg-emerald-600 shadow-emerald-900/40' },
  opleiding: { label: 'Opleiding', icon: BookOpen, badge: 'bg-violet-600 shadow-violet-900/40' },
};

export function datumKort(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

/** "Donderdag 12-11-2026" */
export function datumLang(iso: string): string {
  const d = new Date(iso);
  const dag = d.toLocaleDateString('nl-NL', { weekday: 'long' });
  const datum = d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return `${dag.charAt(0).toUpperCase()}${dag.slice(1)} ${datum}`;
}

export function tijd(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

/** Voorlopige verdeling wallet / eigen bijdrage, gelijk aan de backend-berekening (boekTraining). */
export function verdeling(netto: number, wallet: Wallet | undefined, wkr?: WkrLabel | null): { walletDeel: number; bijbetaling: number } {
  if (!wallet || !wallet.actief) return { walletDeel: 0, bijbetaling: netto };
  // Payroll Bridge: alleen de pot van het WKR-label telt mee (hobby = vrije ruimte, anders professioneel)
  const resterend = wallet.potten
    ? wkr === 'wkr_vrije_ruimte'
      ? wallet.potten.hobby.resterend
      : wallet.potten.pro.resterend
    : wallet.budget_resterend ?? 0;
  let walletDeel = wallet.model === 'on_demand' ? netto : Math.max(0, Math.min(netto, resterend));
  if (wkr === 'wkr_vrije_ruimte' && wallet.hobby_resterend !== null && wallet.hobby_resterend !== undefined) {
    walletDeel = Math.min(walletDeel, Math.max(0, wallet.hobby_resterend));
  }
  walletDeel = Math.round(walletDeel * 100) / 100;
  return { walletDeel, bijbetaling: Math.round((netto - walletDeel) * 100) / 100 };
}

/** Hobby-ruimte (WKR) van dit kalenderjaar is op → geen blokkade, wel een melding: alles gaat via iDEAL. */
export function wkrGrendelActief(t: Pick<AcademyTraining, 'wkr_label'>, wallet: Wallet | undefined): boolean {
  if (!wallet?.actief || t.wkr_label !== 'wkr_vrije_ruimte') return false;
  if (wallet.potten) return wallet.potten.hobby.resterend <= 0;
  return wallet.hobby_resterend !== null && wallet.hobby_resterend !== undefined && wallet.hobby_resterend <= 0;
}
